import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const EmailVerificationPrompt = () => {
  const { user, resendEmailVerification, reloadUser, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const handleResend = async () => {
    setIsResending(true);
    setMessage('');
    
    const result = await resendEmailVerification();
    
    if (result.success) {
      setMessage(result.message || t('emailVerification.messages.emailSent', 'Verification email sent! Please check your inbox and spam folder.'));
    } else {
      setMessage(result.error || t('emailVerification.messages.emailFailed', 'Failed to send verification email. Please try again.'));
    }
    
    setIsResending(false);
  };

  const handleCheckVerification = async () => {
    setIsChecking(true);
    setMessage('');
    
    const result = await reloadUser();
    
    if (result.success && result.user?.emailVerified) {
      setMessage(t('emailVerification.messages.emailVerified', 'Email verified! Redirecting...'));
      setTimeout(() => {
        navigate('/account');
      }, 1500);
    } else {
      setMessage(t('emailVerification.messages.notVerified', 'Email not yet verified. Please check your inbox and click the verification link.'));
    }
    
    setIsChecking(false);
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/signin');
  };

  const languageOptions = [
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' }
  ];
  const activeLanguage = (i18n.language || 'en').startsWith('es') ? 'es' : 'en';

  const handleLanguageChange = (code) => {
    if (code !== activeLanguage) {
      i18n.changeLanguage(code);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#212529',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        backgroundColor: '#2a3138',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
      }}>
        {/* Language Toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'inline-flex',
            borderRadius: '6px',
            border: '1px solid rgba(73, 169, 255, 0.3)',
            overflow: 'hidden'
          }}>
            {languageOptions.map(({ code, label }) => (
              <button
                key={code}
                onClick={() => handleLanguageChange(code)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: activeLanguage === code ? '#49a9ff' : 'transparent',
                  color: activeLanguage === code ? '#fff' : '#f4f7fb',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  transition: 'all 0.3s ease'
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Icon */}
        <div style={{
          textAlign: 'center',
          marginBottom: '24px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto',
            borderRadius: '50%',
            backgroundColor: 'rgba(73, 169, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#49a9ff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 style={{
          color: '#f0f0f0',
          fontSize: '24px',
          fontWeight: '600',
          textAlign: 'center',
          marginBottom: '12px'
        }}>
          {t('emailVerification.title', 'Verify Your Email')}
        </h1>

        {/* Description */}
        <p style={{
          color: '#b0b0b0',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '32px',
          lineHeight: '1.6'
        }}>
          {t('emailVerification.description', 'We\'ve sent a verification email to')} <strong style={{ color: '#f0f0f0' }}>{user?.email}</strong>. {t('emailVerification.instructions', 'Please check your inbox and click the verification link to activate your account.')}
        </p>

        {/* Message */}
        {message && (
          <div style={{
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '24px',
            backgroundColor: (message.includes('sent') || message.includes('enviado') || message.includes('verified') || message.includes('verificado') || message.includes('Redirecting') || message.includes('Redirigiendo')) 
              ? 'rgba(81, 207, 102, 0.1)' 
              : 'rgba(255, 107, 107, 0.1)',
            color: (message.includes('sent') || message.includes('enviado') || message.includes('verified') || message.includes('verificado') || message.includes('Redirecting') || message.includes('Redirigiendo'))
              ? '#51cf66'
              : '#ff6b6b',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {message}
          </div>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <button
            onClick={handleCheckVerification}
            disabled={isChecking}
            style={{
              width: '100%',
              padding: '12px 24px',
              backgroundColor: '#49a9ff',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isChecking ? 'not-allowed' : 'pointer',
              opacity: isChecking ? 0.6 : 1,
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              if (!isChecking) {
                e.target.style.backgroundColor = '#63c6ff';
              }
            }}
            onMouseOut={(e) => {
              if (!isChecking) {
                e.target.style.backgroundColor = '#49a9ff';
              }
            }}
          >
            {isChecking ? t('emailVerification.checking', 'Checking...') : t('emailVerification.verifyButton', 'I\'ve Verified My Email')}
          </button>

          <button
            onClick={handleResend}
            disabled={isResending}
            style={{
              width: '100%',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#49a9ff',
              border: '1px solid #49a9ff',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isResending ? 'not-allowed' : 'pointer',
              opacity: isResending ? 0.6 : 1,
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              if (!isResending) {
                e.target.style.backgroundColor = 'rgba(73, 169, 255, 0.1)';
              }
            }}
            onMouseOut={(e) => {
              if (!isResending) {
                e.target.style.backgroundColor = 'transparent';
              }
            }}
          >
            {isResending ? t('emailVerification.sending', 'Sending...') : t('emailVerification.resendButton', 'Resend Verification Email')}
          </button>

          <button
            onClick={handleSignOut}
            style={{
              width: '100%',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#b0b0b0',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              marginTop: '8px'
            }}
            onMouseOver={(e) => {
              e.target.style.color = '#f0f0f0';
            }}
            onMouseOut={(e) => {
              e.target.style.color = '#b0b0b0';
            }}
          >
            {t('emailVerification.signOut', 'Sign Out')}
          </button>
        </div>

        {/* Help Text */}
        <p style={{
          color: '#888',
          fontSize: '12px',
          textAlign: 'center',
          marginTop: '24px',
          lineHeight: '1.5'
        }}>
          {t('emailVerification.helpText', 'Didn\'t receive the email? Check your spam folder or try resending.')}
        </p>
      </div>
    </div>
  );
};

export default EmailVerificationPrompt;

